import React from 'react';

import { observer } from 'mobx-react';
import { Skeleton } from '@mui/material';

import { BackgroundInformationSections } from 'components/Entities/EntityDetailsTab/styles';
import type { TabComponentProps } from 'components/Entities/EntityDetailsContent/types';
import { EmptyState } from 'components/Entities/EntityAttachmentTab/styles';
import { VatixError } from 'components/Error/types';
import EvidenceFiles, { EvidenceFileRow } from 'components/EvidenceFiles/EvidenceFiles';
import { SLASH_FULL_DATE_FORMAT } from 'core/constants';
import { ControlEvidenceRow } from 'utils/api/modules/risks';
import { formatMomentDate } from 'utils/formatters/timezone';
import { useStore } from 'utils/hooks/store';

const toFileRow = (row: ControlEvidenceRow): EvidenceFileRow | null =>
  row.file
    ? {
        key: row.uuid,
        filename: row.file.filename,
        size: row.file.size,
        url: row.file.url,
        caption: `Submitted by ${row.submittedBy?.name ?? 'N/A'} on ${formatMomentDate(row.submittedAt, SLASH_FULL_DATE_FORMAT)}`,
      }
    : null;

const ControlAttachmentsTab: React.FunctionComponent<TabComponentProps> = ({ store, onError }) => {
  const { controlDetails } = useStore();
  const controlUuid = store.details?.uuid;

  React.useEffect(() => {
    if (!controlUuid) return undefined;
    controlDetails.loadEvidence(controlUuid).then((loaded) => {
      if (!loaded) onError(VatixError.SystemError);
    });
    return () => controlDetails.cancelEvidence();
  }, [controlUuid]);

  const rows = controlDetails.evidence;
  const files = (rows ?? []).flatMap((row) => toFileRow(row) ?? []);

  return (
    <BackgroundInformationSections data-testid="control-attachments-tab">
      {rows === null && <Skeleton variant="rectangular" height={48} />}
      {rows !== null && files.length === 0 && (
        <EmptyState data-testid="control-attachments-empty">
          No attachments yet. Evidence submitted when the control is put in place appears here.
        </EmptyState>
      )}
      <EvidenceFiles files={files} />
    </BackgroundInformationSections>
  );
};

export default observer(ControlAttachmentsTab);
